import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import ArticleCard from "@/components/public/ArticleCard";
import { Reveal } from "@/components/public/Reveal";
import { BrushUnderline } from "@/components/public/Brush";

const STEPS = [
  { n: "01", t: "Pick one small thing to test", d: "Forget the perfect idea. Choose something you can try in a week, with a real person on the other end." },
  { n: "02", t: "Talk to five people before you spend", d: "The fastest way to learn if an idea has legs is a short conversation, not a big build." },
  { n: "03", t: "Get one channel working", d: "Whether it is a marketplace, a social account, or word of mouth, make one way of reaching people work before adding more." },
  { n: "04", t: "Add tools only when they remove real friction", d: "A platform or an app should save you time you can actually feel. Not ten of them. One." },
];

export default function StartHere() {
  const [posts, setPosts] = useState([]);
  useSeo({ title: "Start Here | Lucrative Laptop", description: "New to Lucrative Laptop? Start with these ideas and a simple path forward." });
  useEffect(() => { api.get("/public/posts", { params: { category: "ideas", limit: 3 } }).then((r) => setPosts(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-10 pt-12 pb-12">
      <div className="relative mb-16 text-center">
        <div className="flex items-center justify-center gap-3 mb-5"><span className="w-6 h-0.5 bg-brand" /><span className="label-mono">Start Here</span><span className="w-6 h-0.5 bg-brand" /></div>
        <h1 className="font-poster uppercase text-white leading-[0.88] text-6xl lg:text-8xl">
          YOU CAN <span className="relative inline-block text-brand">LEARN THIS.<BrushUnderline className="absolute left-0 -bottom-3 w-full h-5 text-brand" /></span>
        </h1>
        <p className="text-white/65 text-lg mt-8 max-w-xl mx-auto leading-relaxed">Business can begin with an idea. You do not need to have everything figured out. Here is a simple path to your first real step.</p>
      </div>

      <div className="flex flex-col gap-4 mb-16">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.06}>
            <div className="flex gap-6 items-start border border-ink-600 texture-concrete p-7 edge-card">
              <span className="font-poster text-brand text-5xl lg:text-6xl leading-none shrink-0">{s.n}</span>
              <div><h3 className="font-display font-bold uppercase text-white text-xl lg:text-2xl">{s.t}</h3><p className="text-white/60 mt-2 leading-relaxed">{s.d}</p></div>
            </div>
          </Reveal>
        ))}
      </div>

      {posts.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-poster uppercase text-white text-3xl lg:text-5xl">READ THESE FIRST</h2>
            <Link to="/category/ideas" className="inline-flex items-center gap-1.5 font-cond font-bold uppercase tracking-wider text-white/80 hover:text-brand transition-colors">More ideas <ArrowRight size={15} /></Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">{posts.map((p, i) => <Reveal key={p.slug} delay={i * 0.06}><ArticleCard post={p} variant="default" /></Reveal>)}</div>
        </section>
      )}
    </div>
  );
}
